/* SEARCH-TAG: page-template  */
import React, { useContext, useEffect } from 'react';
import { graphql } from 'gatsby';
import { shape, string } from 'prop-types';
import styled from 'styled-components';
import blogArticleProps from 'TMPropTypes/blogArticleProps';
import Img from 'TMComponents/ImageDynamic';
import Main from 'TMComponents/Layout/Main';
import HeadTags from 'TMComponents/HeadTags';
import NoIndex from 'TMComponents/NoIndex';
import NavLink from 'TMComponents/NavLink';
import { H1, BodyReg } from 'TMComponents/Typography';
import { Bold } from 'TMComponents/Typography/styles';
import AuthorBio from 'TMComponents/AuthorBio';
import ComponentMapper from 'TMComponents/ComponentMapper';
import RelatedArticles from 'TMComponents/RelatedArticles';
import Canonical from 'TMComponents/Canonical';
import { BreadcrumbsContext } from 'TMComponents/Breadcrumbs';
import { breakpoints } from 'TMConstants';
import { BannerContext } from 'TMComponents/Banner';
import { LayoutContext } from 'TMComponents/Layout';
import { getSrc } from 'gatsby-plugin-image';
import { spacing } from 'TMDesignSystem/spacing';
import borderRadius from 'TMDesignSystem/borders';

const Article = styled.article`
    margin: 0 auto;
    max-width: 1120px;
    padding: 0 ${spacing.medium} ${spacing.xLarge};

    @media (min-width: ${breakpoints.mobileTablet}px) {
        padding: 0 ${spacing.large};
        padding-bottom: ${spacing.huge};
    }
`;

const AUTHOR_JOB_TITLE = 'Copywriter at thinkmoney';
const DEFAULT_READ_TIME = '2 minute read';
const THINKMONEY_LOGO_URL =
    'https://www.datocms-assets.com/22599/1586343515-tmweb2020logostrapline.png?auto=compress%2Cformat&dpr=1&fm=png&w=180';

const Title = styled(H1)`
    margin-bottom: ${spacing.mediumLarge};
    margin-top: ${spacing.large};

    @media (min-width: ${breakpoints.mobileTablet}px) {
        margin-top: ${spacing.xxLarge};
    }
`;

const AuthorBioWrapper = styled.div`
    margin-bottom: ${spacing.mediumLarge};
`;

const Image = styled(Img)`
    border-radius: ${borderRadius.small};
    margin-bottom: ${spacing.xLarge};
    max-height: 600px;
    width: 100%;
    object-fit: contain;

    @media (min-width: ${breakpoints.mobileTablet}px) {
        margin-bottom: ${spacing.xxLarge};
        max-height: 650px;
    }
`;

const ArticleInner = styled.div`
    max-width: 100%;
    width: 100%;

    > * {
        margin-bottom: ${spacing.mediumLarge};
    }

    > *:last-child {
        margin-bottom: 0;
    }

    /* Force all nested content to full width */
    * {
        max-width: 100% !important;
    }
`;

const BottomAuthorBio = styled.div`
    margin-top: ${spacing.xxLarge};
    margin-bottom: ${spacing.large};

    @media (min-width: ${breakpoints.mobileTablet}px) {
        margin-top: ${spacing.xxxLarge};
        margin-bottom: ${spacing.xLarge};
    }
`;

const BackText = styled(BodyReg)`
    ${Bold}
    color: ${({ theme }) => theme.colorSystem.primary[4]};
    &:hover {
        text-decoration: underline;
    }
`;

const BlogArticle = ({ data: { datoCmsBlogArticle }, pageContext }) => {
    const {
        noIndex,
        slug,
        title,
        seoMetaTags,
        featuredImage,
        date,
        author,
        categories,
        content,
        relatedPages,
        relatedArticlesSection,
        id
    } = datoCmsBlogArticle;
    const { publishedAt } = pageContext;

    const isBannerAllowed = true;
    const isCrumbsAllowed = true;

    const { setIsNavHidden } = useContext(LayoutContext);
    const { setShowBanner } = useContext(BannerContext);
    const { setCrumbs } = useContext(BreadcrumbsContext);

    const crumbs = [
        { title: 'Blog', link: '/blog/' },
        { title, link: `/blog/${slug}/` }
    ];

    useEffect(() => {
        setIsNavHidden(false);
        setShowBanner(isBannerAllowed);
        setCrumbs({ show: isCrumbsAllowed, crumbList: crumbs });
    }, []);

    const schema = {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': id
        },
        headline: title,
        image: [getSrc(featuredImage)],
        datePublished: publishedAt,
        author: {
            '@type': 'Person',
            name: author.name
        },
        publisher: {
            '@type': 'Organization',
            name: 'thinkmoney',
            logo: {
                '@type': 'ImageObject',
                url: THINKMONEY_LOGO_URL
            }
        }
    };

    return (
        <>
            {noIndex ? <NoIndex /> : <Canonical href={`blog/${slug}`} />}
            <HeadTags tags={seoMetaTags.tags} schemaMarkup={schema} />
            <Main
                isBannerAllowed={isBannerAllowed}
                isCrumbsAllowed={isCrumbsAllowed}
                whiteBackground>
                <Article>
                    <Title variant="xs">{title}</Title>
                    <AuthorBioWrapper>
                        <AuthorBio
                            authorName={author.name}
                            authorSlug={author.slug}
                            jobTitle={author.jobTitle || AUTHOR_JOB_TITLE}
                            authorImage={author.photo?.gatsbyImageData}
                            bio={author.longBio}
                            publishDate={date}
                            readTime={DEFAULT_READ_TIME}
                            tags={categories.map((cat) => cat.name)}
                            variant="top"
                        />
                    </AuthorBioWrapper>
                    <Image imageData={featuredImage} />
                    <ArticleInner>
                        {content.map((item) => (
                            <ComponentMapper key={item.id} {...item} />
                        ))}
                    </ArticleInner>
                    <BottomAuthorBio>
                        <AuthorBio
                            authorName={author.name}
                            authorSlug={author.slug}
                            authorImage={author.photo?.gatsbyImageData}
                            variant="bottom"
                            shareUrl={`https://thinkmoney.co.uk/blog/${slug}/`}
                            shareText={datoCmsBlogArticle.shareText}
                            disclaimerText={
                                datoCmsBlogArticle.disclaimerText
                            }
                        />
                    </BottomAuthorBio>
                    {relatedPages && relatedPages.length > 0 && (
                        <div>
                            {relatedPages.map((item) => (
                                <ComponentMapper key={item.id} {...item} />
                            ))}
                        </div>
                    )}
                    {relatedArticlesSection && (
                        <RelatedArticles
                            title={relatedArticlesSection.title}
                            description={
                                relatedArticlesSection.description
                            }
                            articles={relatedArticlesSection.articles}
                            buttonText={relatedArticlesSection.buttonText}
                            buttonLink={relatedArticlesSection.buttonLink}
                        />
                    )}
                    {!relatedArticlesSection && (
                        <NavLink href="/blog/">
                            <BackText>&lt; Back to articles</BackText>
                        </NavLink>
                    )}
                </Article>
            </Main>
        </>
    );
};

BlogArticle.propTypes = {
    data: shape({
        datoCmsBlogArticle: shape(blogArticleProps)
    }).isRequired,
    pageContext: shape({
        publishedAt: string
    }).isRequired
};

export const pageQuery = graphql`
    query ($slug: String!) {
        datoCmsBlogArticle(slug: { eq: $slug }) {
            seoMetaTags {
                ...GatsbyDatoCmsSeoMetaTags
            }
            id: originalId
            __typename
            noIndex
            slug
            title
            summary
            author {
                name
                slug
                jobTitle
                longBio
                photo {
                    gatsbyImageData(
                        width: 64
                        layout: FIXED
                        imgixParams: { auto: "compress" }
                    )
                }
            }
            date(formatString: "Do MMM YYYY")
            featuredImage {
                alt
                gatsbyImageData(
                    width: 768
                    layout: CONSTRAINED
                    imgixParams: { auto: "compress" }
                )
            }
            categories {
                __typename
                name
            }
            shareText
            disclaimerText
            content {
                ...imageFields
                ...lottieFields
                ...tableFields
                ...textFields
                ...textStructuredFields
                ...videoFields
            }
            relatedPages {
                ...panelFields
            }
            relatedArticlesSection {
                title
                description
                buttonText
                buttonLink
                articles {
                    id
                    title
                    slug
                    summary
                    featuredImage {
                        gatsbyImageData(
                            width: 400
                            layout: CONSTRAINED
                            placeholder: NONE
                        )
                    }
                    categories {
                        name
                    }
                }
            }
        }
    }
`;

export default BlogArticle;
